import { useEffect, useRef, useState } from 'react';
import type { MetaFunction } from 'react-router';
import { data } from 'react-router';
import { Form, redirect, useActionData, useLoaderData } from 'react-router';
import { getMe } from '@/api_clients/trainers/user_client';
import { Button } from '@/components/button';
import { InputEmail } from '@/components/trainers/login/input_email';
import { InputPassword } from '@/components/trainers/login/input_password';
import usePasswordAuth from '@/hooks/use_password_auth';
import EqutumLogoHorizontal from '@/images/equtum_logo_h.svg?react';
import { firebaseConfig } from '@/utils/auth.server';
import { getLoginNotice } from '@/utils/login_notice';
import { authenticate, createLoginCookies } from '@/utils/session.server';
import { setToast } from '@/utils/toast.server';
import { splitParagraph } from '@/utils/view_utils';

export const meta: MetaFunction = () => {
  return [{ title: 'EQUTUM | 厩舎ログイン' }];
};

export const loader = async ({ request }: { request: Request }) => {
  const notice = getLoginNotice(new URL(request.url).searchParams.get('error'));

  // notice 付きの遷移は _authed 側からの差し戻し。Cookie は有効なままなので、
  // ここで / に戻すと / ⇄ /login のループになる。
  const token = notice ? null : await authenticate(request).catch(() => null);
  if (token) {
    return redirect('/');
  }

  return {
    firebaseConfig,
    notice,
  };
};

export default function Login() {
  const { firebaseConfig, notice } = useLoaderData<typeof loader>();
  const actionData = useActionData<typeof action>();
  const [formIsSubmitting, setFormIsSubmitting] = useState(false);
  const {
    user,
    error,
    signIn,
    setEmail,
    setPassword,
    isSubmitting: passwordIsSubmitting,
  } = usePasswordAuth(firebaseConfig);
  const isSubmitting = passwordIsSubmitting || formIsSubmitting;

  const formRef = useRef<HTMLFormElement>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [expirationTime, setExpirationTime] = useState<string | null>(null);

  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (user) {
      user.getIdTokenResult().then((idTokenResult) => {
        setIdToken(idTokenResult.token);
        setRefreshToken(user.refreshToken);
        setExpirationTime(idTokenResult.expirationTime);
      });
    }
  }, [user]);

  useEffect(() => {
    if (idToken && refreshToken && expirationTime) {
      setFormIsSubmitting(true);
      formRef.current?.submit();
    }
  }, [idToken, refreshToken, expirationTime]);

  useEffect(() => {
    setFormIsSubmitting(false);
  }, [error, actionData?.error]);

  return (
    <div className="bg-surface-base mx-auto flex min-h-svh w-full flex-col items-center overflow-hidden">
      <div className="flex w-full max-w-xs flex-col items-center">
        <EqutumLogoHorizontal />
        <div className="mt-6 flex w-full flex-col whitespace-nowrap">
          <div className="text-glyphs-normal w-full gap-5 text-3xl leading-tight font-bold">
            厩舎ログイン
          </div>
        </div>
        <div className="text-negative">
          {actionData?.error && <div key={actionData.error}>{actionData.error}</div>}
          {error && <div>{error.message}</div>}
          {!actionData?.error && !error && notice && <div>{splitParagraph(notice)}</div>}
        </div>
        <Form
          ref={formRef}
          className="flex w-full flex-col items-start gap-4"
          method="post"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              buttonRef.current?.click();
            }
          }}
        >
          {idToken && refreshToken && expirationTime && (
            <>
              <input name="id_token" type="hidden" value={idToken} />
              <input name="refresh_token" type="hidden" value={refreshToken} />
              <input name="expiration_time" type="hidden" value={expirationTime} />
            </>
          )}
          <div className="mt-6 flex w-full flex-col">
            <InputEmail setEmail={setEmail} />
            <InputPassword setPassword={setPassword} />
          </div>
          <div className="mt-6 flex max-w-full flex-col items-center self-center whitespace-nowrap">
            <Button
              ref={buttonRef}
              className="disabled:bg-disabled min-h-[40px] w-60 max-w-full gap-1 rounded-sm bg-indigo-500 px-4 py-3 text-center text-base leading-none font-medium tracking-wider text-white"
              color="primary"
              disabled={isSubmitting}
              size="large"
              type="button"
              variant="filled"
              onClick={signIn}
            >
              {isSubmitting ? 'ログイン中...' : 'ログイン'}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
}

export const action = async ({ request }: { request: Request }) => {
  const formData = await request.formData();
  const idToken = formData.get('id_token') as string;
  const refreshToken = formData.get('refresh_token') as string;
  const expirationTime = new Date(formData.get('expiration_time') as string);
  try {
    const me = await getMe(idToken);
    if (me === null) {
      return data(
        { error: '登録されたユーザーが見つかりませんでした。アカウント情報をお確かめください。' },
        { status: 400 }
      );
    }
  } catch (error) {
    return data(
      { error: '登録されたユーザーが見つかりませんでした。アカウント情報をお確かめください。' },
      { status: 400 }
    );
  }

  try {
    const cookies = createLoginCookies(idToken, refreshToken, expirationTime);
    const headers = await setToast({ type: 'success', content: 'ログインに成功しました' }, request);

    for (const cookie of cookies) {
      headers.append('Set-Cookie', cookie);
    }
    return redirect('/', {
      headers,
    });
  } catch (error) {
    console.error(error);
    return data({ error: 'メールアドレスまたはパスワードが誤っています' }, { status: 400 });
  }
};
